import axios from "axios";
import { useState } from "react";
import { format, isAfter } from "date-fns";
import { useAlert } from "./useAlert";

const API_URL = process.env.REACT_APP_API_URL;

export function useAppointments() {
    const [appointments, setAppointments] = useState([]);
    const [allAppointments, setAllAppointments] = useState([]);
    const [customerAppointments, setCustomerAppointments] = useState([]);
    const { showAlert } = useAlert();

    const fetchAppointments = async (id, includePast = false, limit = 3) => {
        try {
            const res = await axios({
                method: "GET",
                url: id ? `${API_URL}/appointments/customer/${id}` : `${API_URL}/appointments`,
                withCredentials: true
            });
            
            const allAppointments = res.data.appointments || [];

            const enrichedAppointments = allAppointments.map(appointment => ({
                ...appointment,
                customerName: appointment.customer?.prename + " " + appointment.customer?.name,
            }));

            const now = new Date();
            const visibleAppointments = includePast
                ? enrichedAppointments
                : enrichedAppointments.filter(appointment => isAfter(new Date(appointment.date), now));

            const sortedAppointments = visibleAppointments.sort((a, b) => new Date(a.date) - new Date(b.date));

            const formattedAppointments = sortedAppointments.map((appointment) => ({
                formattedDate: format(new Date(appointment.date), "dd.MM.yyyy HH:mm"),
                description: appointment.description,
                customerName: appointment.customerName,
                _id: appointment._id,
                date: appointment.date
            }));
            
            setAppointments(limit ? formattedAppointments.slice(0, limit) : formattedAppointments);
        } catch (error) {
            showAlert('Fehler beim Laden der Termine', 'error');
        }
    };

    const fetchAllAppointments = async (limit = null) => {
        try {
            const res = await axios({
                method: "GET",
                url: `${API_URL}/appointments`,
                withCredentials: true
            });
            
            const allAppointment = res.data.appointments || [];

            const enrichedAppointments = allAppointment.map(appointment => ({
                ...appointment,
                customerName: appointment.customer?.prename + " " + appointment.customer?.name,
            }));

            const sortedAppointments = enrichedAppointments.sort((a, b) => new Date(a.date) - new Date(b.date));

            const formattedAppointments = sortedAppointments.map((appointment) => ({
                formattedDate: format(new Date(appointment.date), "dd.MM.yyyy HH:mm"),
                description: appointment.description,
                customerName: appointment.customerName,
                _id: appointment._id,
                date: appointment.date
            }));
            
            setAllAppointments(limit ? formattedAppointments.slice(0, limit) : formattedAppointments);
        } catch (error) {
            showAlert('Fehler beim Laden der Termine', 'error');
        }
    }

    const deleteAppointment = async (id) => {
        const confirmDelete = window.confirm(
            'Möchten Sie den Termin wirklich löschen?'
        );
        if (!confirmDelete) return;
    
        try {
            await axios.delete(`${API_URL}/appointments/${id}`, {
                withCredentials: true
            });
    
            setAppointments(prev => prev.filter(appointment => appointment._id !== id));
            setTimeout(() => window.location.reload(), 1000);
            showAlert('Termin erfolgreich gelöscht', 'success');
        } catch (err) {
            showAlert('Fehler beim Löschen des Termins', 'error');
        }
    };

    const updateAppointment = async (id, updatedData) => {
        try {
            const response = await axios.patch(`/api/v1/appointments/${id}`, updatedData);
            const updatedAppointment = response.data.data.appointment;

            setAppointments((prevAppointments) =>
                prevAppointments.map((appointment) =>
                    appointment._id === id ? updatedAppointment : appointment
                )
            );
            showAlert('Termin erfolgreich aktualisiert.', 'success');
        } catch (error) {
            showAlert('Fehler beim Aktualisieren des Termins', 'error');
        }
    };

    const fetchCustomerAppointment = async (id) => {
        try {
            const appointmentData = await axios.get(`${API_URL}/appointments/${id}`, { withCredentials: true });
            setCustomerAppointments(appointmentData.data.data);
        } catch (error) {                
            showAlert("Fehler beim Laden der Termindetails", "error");
        }
    }

    const createAppointment = async (startDate, description, id) => {
        if(!startDate || !description) return showAlert('Bitte alle Felder eintragen.', 'error');
        try {
            const res = await axios({
                method: 'POST',
                url: `${API_URL}/appointments/customer/${id}`,
                withCredentials: true,
                data: {
                    date: startDate,
                    description
                }
            });
            if (res.data.status === 'success') {
                showAlert('Termin erfolgreich erstellt', 'success');
            
                setTimeout(() => window.location.reload(), 1000);
            } else {
                showAlert('Fehler beim Erstellen des Termins', 'error');
            }
        } catch (error) {
            showAlert('Fehler beim Erstellen des Termins', 'error');
        }
    };

    return { fetchAppointments, fetchAllAppointments, appointments, allAppointments, deleteAppointment, updateAppointment, fetchCustomerAppointment, customerAppointments, createAppointment  }
}